import { useState, useCallback } from 'react';
import { Link } from '@inertiajs/react';
import { route } from '../../utils/route';
import type { ProductListing } from '../../types/products';

interface RelatedProductCardProps {
  product: ProductListing;
  onAddToCart: (productId: number, formatId: number, quantity: number) => Promise<void>;
}

function formatPrice(price: number): string {
  return price.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function RelatedProductCard({ product, onAddToCart }: RelatedProductCardProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [added, setAdded] = useState(false);

  const format = product.default_format ?? product.formats[0] ?? null;
  const detailUrl = product.slug
    ? route('product.show', { slug: product.slug })
    : `/producto/${product.id}`;

  const handleAdd = useCallback(async () => {
    if (!format || isAdding) return;
    setIsAdding(true);
    try {
      await onAddToCart(product.id, format.id, 1);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch {
      // handled upstream
    } finally {
      setIsAdding(false);
    }
  }, [format, isAdding, product.id, onAddToCart]);

  if (!format) return null;

  return (
    <div className="flex w-[160px] flex-shrink-0 flex-col overflow-hidden rounded-xl bg-white shadow-sm sm:w-[200px]" style={{ scrollSnapAlign: 'start' }}>
      <Link href={detailUrl} className="block">
        {product.has_image ? (
          <img
            src={product.webp_image_url}
            alt={product.title}
            loading="lazy"
            className="aspect-square w-full bg-white object-contain"
          />
        ) : (
          <div className="flex aspect-square w-full items-center justify-center bg-gray-50">
            <i className="fa-solid fa-leaf text-3xl text-gray-300" />
          </div>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-2.5 sm:p-3">
        <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-400">
          {product.category_name}
        </div>
        <Link
          href={detailUrl}
          className="mb-1 line-clamp-2 text-[13px] font-semibold leading-tight text-gray-800 no-underline hover:text-green-700 sm:text-sm"
          style={{ minHeight: '34px' }}
        >
          {product.title}
        </Link>
        <div className="mb-2 text-xs text-gray-500">{format.name}</div>
        <div className="mb-3 text-base font-bold text-green-600 sm:text-lg">
          ${formatPrice(format.discounted_price)}
        </div>

        <div className="mt-auto flex flex-col gap-1.5 sm:flex-row sm:gap-2">
          <Link
            href={detailUrl}
            className="rounded-md bg-gray-100 px-2 py-2 text-center text-xs font-medium text-gray-600 no-underline transition-colors hover:bg-gray-200 hover:text-gray-800 sm:flex-1 sm:text-[13px]"
          >
            Ver
          </Link>
          <button
            type="button"
            onClick={handleAdd}
            disabled={isAdding}
            className={`flex items-center justify-center gap-1 rounded-md px-2 py-2 text-xs font-semibold text-white transition-colors sm:flex-1 sm:text-[13px] ${
              added ? 'bg-green-700' : 'bg-green-600 hover:bg-green-700 active:bg-green-800'
            }`}
          >
            {isAdding ? (
              <i className="fa fa-spinner fa-spin" />
            ) : added ? (
              <i className="fa fa-check" />
            ) : (
              <>
                <i className="fa fa-cart-plus" />
                Agregar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
